import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api/client';
import { isMaster } from '../utils/auth';

const KST_OFFSET = '+09:00';
const PRODUCTS = [
  { id: 'daily_subscription', name: '모던워시 데일리' },
  { id: 'all_in_one_subscription', name: '모던워시 올인원' },
];
const PERIODS = [
  { key: '1m', label: '1개월', months: 1 },
  { key: '3m', label: '3개월', months: 3 },
  { key: '6m', label: '6개월', months: 6 },
  { key: '12m', label: '12개월', months: 12 },
];

const todayStr = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const addMonthsAsPeriodEnd = (startStr, months) => {
  const [year, month, day] = startStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  const targetMonth = month - 1 + months;
  const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  return `${year + Math.floor(targetMonth / 12)}-${String((targetMonth % 12) + 1).padStart(2, '0')}-${String(Math.min(day, lastDay) - 1).padStart(2, '0')}`;
};

const messageOf = (error, fallback) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join('\n') : message || fallback;
};

const recipientName = (member) => member?.memberNickname || member?.memberName || '이름 없음';

const BulkGrantMembershipDialog = ({ open, onClose, onGranted, initialMembers = [] }) => {
  const master = isMaster();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [recipients, setRecipients] = useState([]);
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(addMonthsAsPeriodEnd(todayStr(), 1));
  const [preset, setPreset] = useState('1m');
  const [reason, setReason] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const addRecipient = async (member) => {
    if (recipients.some((recipient) => recipient.member._id === member._id)) return;
    if (recipients.length >= 50) {
      setError('한 번에 최대 50명까지 선택할 수 있습니다.');
      return;
    }
    setLoadingIds((current) => new Set(current).add(member._id));
    setError('');
    try {
      const [vehicleRes, subscriptionRes] = await Promise.all([
        api.get(`/member/admin/members/${member._id}/vehicles`),
        api.get(`/member/admin/members/${member._id}/subscriptions`),
      ]);
      const vehicles = vehicleRes.data || [];
      const active = (subscriptionRes.data || []).find(
        (subscription) => subscription.status === 'active' || subscription.status === 'cancellation_requested',
      );
      const vehicleId = (vehicles.find((vehicle) => vehicle.isPrimary) || vehicles[0])?._id || '';
      const issue = active
        ? '이미 이용 중인 멤버십이 있습니다.'
        : (!vehicleId ? '등록된 차량이 없습니다.' : '');
      setRecipients((current) => [...current, { member, vehicles, vehicleId, issue }]);
    } catch (requestError) {
      setError(`${recipientName(member)}님의 차량·구독 정보를 불러오지 못했습니다. ${messageOf(requestError, '')}`.trim());
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(member._id);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    const today = todayStr();
    setQuery(''); setSearchResults([]); setRecipients([]); setError(''); setResult(null);
    setProductId(PRODUCTS[0].id); setStartDate(today); setEndDate(addMonthsAsPeriodEnd(today, 1));
    setPreset('1m'); setReason(''); setEvidenceNote(''); setStoreId('');
  }, [open]);

  useEffect(() => {
    if (!open || initialMembers.length === 0) return;
    // 회원 목록에서 체크한 대상은 추가 검색 없이 즉시 차량·구독을 조회한다.
    initialMembers.slice(0, 50).forEach((member) => { void addRecipient(member); });
    // 대화상자가 열릴 때의 선택 목록만 사용한다. 열린 뒤 목록 변경은 사용자가 직접 제어한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !master) return undefined;
    let cancelled = false;
    api.get('/stores/admin').then((res) => {
      if (!cancelled) setStores(res.data || []);
    }).catch(() => {
      if (!cancelled) setStores([]);
    });
    return () => { cancelled = true; };
  }, [open, master]);

  const readyRecipients = useMemo(
    () => recipients.filter((recipient) => !recipient.issue && recipient.vehicleId),
    [recipients],
  );

  const searchMembers = async (event) => {
    event?.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setError('');
    try {
      const res = await api.get('/member/admin/members', { params: { page: 1, limit: 20, search: query.trim() } });
      setSearchResults(res.data?.members || []);
    } catch (requestError) {
      setError(messageOf(requestError, '회원 검색에 실패했습니다.'));
    } finally {
      setSearching(false);
    }
  };

  const removeRecipient = (memberId) => {
    setRecipients((current) => current.filter((recipient) => recipient.member._id !== memberId));
  };

  const updateVehicle = (memberId, vehicleId) => {
    setRecipients((current) => current.map((recipient) => (
      recipient.member._id === memberId
        ? { ...recipient, vehicleId, issue: vehicleId ? (recipient.issue === '등록된 차량이 없습니다.' ? '' : recipient.issue) : '대상 차량을 선택해주세요.' }
        : recipient
    )));
  };

  const changePreset = (_, next) => {
    if (!next) return;
    setPreset(next);
    if (next === 'custom') return;
    const period = PERIODS.find((item) => item.key === next);
    if (period) setEndDate(addMonthsAsPeriodEnd(startDate, period.months));
  };

  const changeStartDate = (value) => {
    setStartDate(value);
    const period = PERIODS.find((item) => item.key === preset);
    if (period) setEndDate(addMonthsAsPeriodEnd(value, period.months));
  };

  const submit = async () => {
    if (!reason.trim()) { setError('지급 사유를 입력해주세요.'); return; }
    if (readyRecipients.length === 0) { setError('지급 가능한 회원을 한 명 이상 선택해주세요.'); return; }
    setSubmitting(true); setError(''); setResult(null);
    try {
      const response = await api.post('/subscriptions/admin/subscriptions/grant-batch', {
        productId,
        startDate: `${startDate}T00:00:00.000${KST_OFFSET}`,
        endDate: `${endDate}T23:59:59.999${KST_OFFSET}`,
        grantReason: reason.trim(),
        ...(evidenceNote.trim() ? { evidenceNote: evidenceNote.trim() } : {}),
        ...(master && storeId ? { storeId } : {}),
        recipients: readyRecipients.map((recipient) => ({ userId: recipient.member._id, vehicleId: recipient.vehicleId })),
      });
      setResult(response.data);
      if ((response.data?.granted || []).length > 0) onGranted?.();
    } catch (requestError) {
      setError(messageOf(requestError, '이용권 일괄 지급에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <GroupAddIcon color="primary" /> 여러 명 이용권 부여
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          선택한 회원마다 차량·기존 구독을 확인한 뒤 결제 없이 이용권을 지급합니다. 한 명이 실패해도 다른 회원의 지급 결과는 유지되며, 결과를 아래에서 확인할 수 있습니다.
        </Alert>

        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>1. 대상 회원 선택</Typography>
        <Box component="form" onSubmit={searchMembers} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField size="small" fullWidth value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름, 이메일, 전화번호로 검색" disabled={submitting} />
          <Button type="submit" variant="outlined" startIcon={<SearchIcon />} disabled={searching || !query.trim()}>검색</Button>
        </Box>
        {searchResults.length > 0 && (
          <Paper variant="outlined" sx={{ maxHeight: 180, overflowY: 'auto', mb: 1.5 }}>
            {searchResults.map((member) => {
              const selected = recipients.some((recipient) => recipient.member._id === member._id);
              const loading = loadingIds.has(member._id);
              return (
                <Box key={member._id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                  <Checkbox checked={selected} disabled={loading || submitting} onChange={() => (selected ? removeRecipient(member._id) : addRecipient(member))} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>{recipientName(member)}</Typography>
                    <Typography variant="caption" color="text.secondary">{member.memberEmail || '-'} · {member.memberPhone || '-'}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        )}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {recipients.map((recipient) => (
            <Paper key={recipient.member._id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{recipientName(recipient.member)}</Typography>
                  <Typography variant="caption" color="text.secondary">{recipient.member.memberEmail || '-'}</Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 180 }} disabled={Boolean(recipient.issue && recipient.issue !== '대상 차량을 선택해주세요.') || submitting}>
                  <InputLabel>지급 차량</InputLabel>
                  <Select value={recipient.vehicleId} label="지급 차량" onChange={(event) => updateVehicle(recipient.member._id, event.target.value)}>
                    {recipient.vehicles.map((vehicle) => <MenuItem key={vehicle._id} value={vehicle._id}>{vehicle.licensePlate}{vehicle.isPrimary ? ' (대표)' : ''}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button size="small" color="inherit" onClick={() => removeRecipient(recipient.member._id)} disabled={submitting}>제외</Button>
              </Stack>
              {recipient.issue && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>{recipient.issue}</Typography>}
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>2. 공통 지급 내용</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <FormControl size="small" fullWidth><InputLabel>이용권 상품</InputLabel><Select value={productId} label="이용권 상품" onChange={(event) => setProductId(event.target.value)}>{PRODUCTS.map((product) => <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>)}</Select></FormControl>
          {master && <FormControl size="small" fullWidth><InputLabel>귀속 매장 (선택)</InputLabel><Select value={storeId} label="귀속 매장 (선택)" onChange={(event) => setStoreId(event.target.value)}><MenuItem value="">기본 매장</MenuItem>{stores.map((store) => <MenuItem key={store._id} value={store._id}>{store.name}</MenuItem>)}</Select></FormControl>}
        </Stack>
        <ToggleButtonGroup value={preset} exclusive onChange={changePreset} size="small" color="primary" sx={{ mb: 1.5, flexWrap: 'wrap' }} disabled={submitting}>
          {PERIODS.map((period) => <ToggleButton key={period.key} value={period.key}>{period.label}</ToggleButton>)}
          <ToggleButton value="custom">직접 지정</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField label="시작일" type="date" size="small" fullWidth value={startDate} onChange={(event) => changeStartDate(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="종료일" type="date" size="small" fullWidth value={endDate} onChange={(event) => { setEndDate(event.target.value); setPreset('custom'); }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startDate } }} />
        </Stack>
        <TextField id="bulk-grant-reason" label="지급 사유 (필수)" required multiline minRows={2} fullWidth size="small" value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} helperText={`${reason.length}/200`} sx={{ mb: 1.5 }} />
        <TextField id="bulk-grant-evidence" label="증빙 메모 (선택)" fullWidth size="small" value={evidenceNote} onChange={(event) => setEvidenceNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />

        {recipients.length > readyRecipients.length && <Alert severity="warning" sx={{ mt: 2 }}>선택한 {recipients.length}명 중 {readyRecipients.length}명만 지급 가능합니다. 오류가 표시된 대상은 서버에 보내지 않습니다.</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
        {result && (
          <Alert severity={result.failed?.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
            {result.total}명 중 {result.granted?.length || 0}명에게 지급했습니다.
            {result.failed?.length > 0 && <Box component="ul" sx={{ mb: 0, mt: 0.75, pl: 2.5 }}>{result.failed.map((failed) => <li key={failed.userId}>{recipients.find((recipient) => recipient.member._id === failed.userId)?.member && recipientName(recipients.find((recipient) => recipient.member._id === failed.userId)?.member)}: {failed.message}</li>)}</Box>}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Chip size="small" label={`지급 가능 ${readyRecipients.length}명`} color={readyRecipients.length ? 'primary' : 'default'} variant="outlined" sx={{ mr: 'auto' }} />
        <Button onClick={onClose} disabled={submitting}>닫기</Button>
        <Button variant="contained" onClick={submit} disabled={submitting || readyRecipients.length === 0 || !reason.trim()}>{submitting ? '지급 중…' : `${readyRecipients.length}명 지급`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkGrantMembershipDialog;
